import type { PlatformType } from "./utils-v2";
import { getBooksWithFieldGroups } from "./sportsbook-field-groups";
import { normalizeSlipTypeId, isExplicitSlipTypePlatform } from "./props-slip-breakevens";
import { GLOBAL_ACTIONABLE_BOOK_IDS } from "@/lib/sportsbook-preferences";
import { normalizeSportsbookId } from "@/lib/sportsbook-registry";
import { oddsRangeActive, orderOddsRange, parseAmericanBound } from "./odds-range";
import { activeGameDays, coerceGameDays, coerceStartsWithin, localTodayYmd } from "@/lib/game-date";
import { keepLinkMarker, markLink } from "@/lib/tool-link";
import { WHALES_COLUMN, type ComparisonColumnId } from "@/lib/whales";

export type SortDirection = "asc" | "desc";
export type MarketScope = "" | "player" | "game";
export type OverUnder = "" | "over" | "under";

export type FilterChangeSource =
  | "bar"
  | "sheet"
  | "preset"
  | "clear_all"
  | "book_strip";

export type FilterChangeOptions = {
  immediate?: boolean;
  source?: FilterChangeSource;
  presetId?: string;
};

type ParsedWeights = Record<string, number>;

export interface OptimizerQueryState {
  sportsbook: string;
  platform: PlatformType;
  sort: string;
  order: SortDirection;
  playerSearch: string;
  leagues: string[];
  statTypes: string[];
  matchups: string[];
  marketScope: MarketScope;
  slipType: string;
  limit: number;
  offset: number;

  minEV: number;
  gameDays: string[];
  startsWithin: number;
  live: boolean;

  minDataPoints: number;
  minLiquidity: number;
  minOddsToHit: number;
  maxOddsToHit: number;
  minOdds: number | null;
  maxOdds: number | null;
  requiredComparisonBooks: ComparisonColumnId[];
  nocache: boolean;

  weights: ParsedWeights | null;

  preset: string | null;

  requiredBooks: string[];

  hidePushProps: boolean;
  overUnder: OverUnder;
  boost: number;
}

const REQUIRED_BOOK_OPTIONS: ReadonlySet<string> = new Set<ComparisonColumnId>([WHALES_COLUMN, ...getBooksWithFieldGroups()]);

export function canonicalComparisonBooks(value: unknown): ComparisonColumnId[] {
  const values: unknown[] = typeof value === "string" ? [value] : Array.isArray(value) ? value : [];
  const books = values
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim().toLowerCase())
    .filter((item): item is ComparisonColumnId => REQUIRED_BOOK_OPTIONS.has(item));
  return [...new Set(books)].sort();
}

const DEFAULT_SORT = "ev_percentage";
const DEFAULT_ORDER: SortDirection = "desc";
export const DEFAULT_LIMIT = 100;
export const FANTASY_PROPS_DEFAULT_SORT = "consensus_implied_probability";

export function sanitizeFantasyPropsSortColumn(column: string): string {
  return column === DEFAULT_SORT ? FANTASY_PROPS_DEFAULT_SORT : column;
}

export function sanitizeFantasyPropsQueryState(
  state: OptimizerQueryState,
): OptimizerQueryState {
  const sort = sanitizeFantasyPropsSortColumn(state.sort);
  if (state.minEV === 0 && state.marketScope === "" && state.sort === sort) {
    return state;
  }

  return {
    ...state,
    minEV: 0,
    marketScope: "",
    sort,
  };
}

function normalizeMarketScope(raw: string | undefined): MarketScope {
  const value = raw?.trim().toLowerCase();
  return value === "player" || value === "game" ? value : "";
}

function normalizeOverUnder(raw: string | undefined): OverUnder {
  const value = raw?.trim().toLowerCase();
  return value === "over" || value === "under" ? value : "";
}

function ensureArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
}

function firstScalarParam(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.find(
      (item) => typeof item === "string" && item.trim().length > 0,
    );
  }
  return value;
}

function isPlatformType(value: string): value is PlatformType {
  return GLOBAL_ACTIONABLE_BOOK_IDS.includes(value as PlatformType);
}

function normalizeParamToString(value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) return value.join(",");
  return value;
}

function parseNumber(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeLimit(raw: string | undefined): number {
  if (!raw) return DEFAULT_LIMIT;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_LIMIT;
  }
  return parsed;
}

function parseWeights(raw: string | undefined): ParsedWeights | null {
  if (!raw) return null;

  const map: ParsedWeights = {};
  raw.split(",").forEach(part => {
    const [bookRaw, weightRaw] = part.split(":");
    const book = bookRaw?.trim().toLowerCase();
    if (!book) return;
    const weight = weightRaw ? Number(weightRaw) : 1;
    if (!Number.isFinite(weight) || weight <= 0) return;
    map[book] = weight;
  });

  return Object.keys(map).length > 0 ? map : null;
}

function normalizeOffset(raw: string | undefined): number {
  if (!raw) return 0;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
}

export function normalizeQueryState(
  raw: Record<string, string | string[] | undefined>
): OptimizerQueryState {
  const sportsbookSelections = ensureArray(raw.sportsbook);
  const primarySelection =
    sportsbookSelections
      .map((item) => normalizeSportsbookId(item) || (item ?? "").toLowerCase())
      .find((item) => item.length > 0) ?? "novig";
  const sportsbook = primarySelection;
  const platform = GLOBAL_ACTIONABLE_BOOK_IDS.includes(sportsbook as PlatformType)
    ? (sportsbook as PlatformType)
    : "novig";

  const sort = firstScalarParam(raw.sort) ?? DEFAULT_SORT;
  const orderRaw = firstScalarParam(raw.order)?.toLowerCase();
  const order: SortDirection = orderRaw === "asc" ? "asc" : DEFAULT_ORDER;

  const leagues = ensureArray(raw.league);
  const statTypes = ensureArray(raw.stat_type);
  const matchups = ensureArray(raw.matchup);
  const marketScope = normalizeMarketScope(firstScalarParam(raw.market_type));

  const rawSlipType = firstScalarParam(raw.slip_type) ?? "";
  const slipType = normalizeSlipTypeId(platform, rawSlipType);
  const limit = normalizeLimit(firstScalarParam(raw.limit));
  const offset = normalizeOffset(firstScalarParam(raw.offset));

  const minEV = Math.max(0, parseNumber(firstScalarParam(raw.min_ev), 0));
  const gameDays = coerceGameDays(ensureArray(raw.game_date));
  const startsWithin = coerceStartsWithin(firstScalarParam(raw.starts_within));
  const minDataPoints = parseNumber(firstScalarParam(raw.min_data_points), 0);
  const minLiquidity = Math.max(0, Math.round(parseNumber(firstScalarParam(raw.min_liquidity), 0)));
  let minOddsToHit = clampNumber(parseNumber(firstScalarParam(raw.min_odds_to_hit), 0), 0, 100);
  let maxOddsToHit = clampNumber(parseNumber(firstScalarParam(raw.max_odds_to_hit), 100), 0, 100);
  if (minOddsToHit > maxOddsToHit) {
    [minOddsToHit, maxOddsToHit] = [maxOddsToHit, minOddsToHit];
  }
  const { minOdds, maxOdds } = orderOddsRange(
    parseAmericanBound(firstScalarParam(raw.min_odds)),
    parseAmericanBound(firstScalarParam(raw.max_odds)),
  );
  const requiredComparisonBooks = canonicalComparisonBooks(raw.required_book);
  const nocacheRaw = firstScalarParam(raw.nocache)?.toLowerCase();
  const nocache = nocacheRaw === "1" || nocacheRaw === "true";

  const rawWeights = normalizeParamToString(raw.weights);
  const weights = parseWeights(rawWeights);

  const preset = firstScalarParam(raw.preset)?.trim() || null;
  const rawRequiredBooks = normalizeParamToString(raw.required_books);
  const requiredBooks = rawRequiredBooks
    ? rawRequiredBooks
        .split(",")
        .map((b) => b.trim().toLowerCase())
        .filter(Boolean)
    : [];

  const hidePushPropsRaw = firstScalarParam(raw.hide_push_props)?.toLowerCase();
  const hidePushProps = hidePushPropsRaw === "1" || hidePushPropsRaw === "true";
  const overUnder = normalizeOverUnder(firstScalarParam(raw.over_under));
  const boost = clampNumber(parseNumber(firstScalarParam(raw.boost), 0), 0, 100);

  return {
    sportsbook,
    platform,
    sort,
    order,
    playerSearch: firstScalarParam(raw.player_search) ?? "",
    leagues,
    statTypes,
    matchups,
    marketScope,
    slipType,
    limit,
    offset,
    minEV,
    gameDays,
    startsWithin,
    live: false,
    minDataPoints,
    minLiquidity,
    minOddsToHit,
    maxOddsToHit,
    minOdds,
    maxOdds,
    requiredComparisonBooks,
    nocache,
    weights,
    preset,
    requiredBooks,
    hidePushProps,
    overUnder,
    boost,
  };
}

export function queryStateFromSearch(search: string): OptimizerQueryState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw: Record<string, string | string[] | undefined> = {};
  params.forEach((value, key) => {
    const current = raw[key];
    if (current === undefined) {
      raw[key] = value;
    } else if (Array.isArray(current)) {
      raw[key] = [...current, value];
    } else {
      raw[key] = [current, value];
    }
  });
  return normalizeQueryState(raw);
}

export function canonicalizeSportsbookPlatformState(
  state: OptimizerQueryState,
  options: {
    allowedSportsbooks?: readonly PlatformType[];
    fallbackPlatform?: PlatformType;
  } = {},
): OptimizerQueryState {
  const allowedSportsbooks =
    options.allowedSportsbooks && options.allowedSportsbooks.length > 0
      ? options.allowedSportsbooks
      : GLOBAL_ACTIONABLE_BOOK_IDS;
  const allowed = new Set(
    allowedSportsbooks.map((book) => book.trim().toLowerCase()),
  );

  const sportsbook = state.sportsbook.trim().toLowerCase();
  const fallback = options.fallbackPlatform?.trim().toLowerCase() ?? "";
  const currentPlatform = state.platform.trim().toLowerCase();

  const firstAllowed = allowedSportsbooks
    .map((book) => book.trim().toLowerCase())
    .find((book) => isPlatformType(book));

  const resolved =
    sportsbook && isPlatformType(sportsbook) && allowed.has(sportsbook)
      ? sportsbook
      : fallback && isPlatformType(fallback) && allowed.has(fallback)
        ? fallback
        : firstAllowed
          ? firstAllowed
          : currentPlatform && isPlatformType(currentPlatform)
            ? currentPlatform
            : "novig";
  const platform = resolved as PlatformType;

  return {
    ...state,
    sportsbook: resolved,
    platform,
    slipType: normalizeSlipTypeId(platform, state.slipType),
    requiredComparisonBooks: state.requiredComparisonBooks.filter((book) => book !== platform),
    requiredBooks: state.requiredBooks.filter((book) => book.trim().toLowerCase() !== platform),
  };
}

export function serializeQueryState(state: OptimizerQueryState): URLSearchParams {
  const params = new URLSearchParams();

  params.set("sportsbook", state.sportsbook || "novig");
  params.set("sort", state.sort || DEFAULT_SORT);
  params.set("order", state.order || DEFAULT_ORDER);

  if (state.playerSearch) {
    params.set("player_search", state.playerSearch);
  }

  state.leagues.forEach((league) => {
    params.append("league", league);
  });

  state.statTypes.forEach((stat) => {
    params.append("stat_type", stat);
  });

  state.matchups.forEach((matchup) => {
    params.append("matchup", matchup);
  });

  if (state.marketScope) {
    params.set("market_type", state.marketScope);
  }

  if (state.slipType) {
    params.set("slip_type", state.slipType);
  }

  params.set("limit", state.limit.toString());
  if (state.offset > 0) {
    params.set("offset", state.offset.toString());
  }

  if (state.minEV > 0) {
    params.set("min_ev", state.minEV.toString());
  }

  state.gameDays.forEach((day) => {
    params.append("game_date", day);
  });

  if (state.startsWithin > 0) {
    params.set("starts_within", state.startsWithin.toString());
  }

  if (state.minDataPoints > 0) {
    params.set("min_data_points", state.minDataPoints.toString());
  }

  if (state.minLiquidity > 0) {
    params.set("min_liquidity", state.minLiquidity.toString());
  }

  if (state.minOddsToHit > 0) {
    params.set("min_odds_to_hit", state.minOddsToHit.toString());
  }

  if (state.maxOddsToHit < 100) {
    params.set("max_odds_to_hit", state.maxOddsToHit.toString());
  }

  if (state.minOdds !== null) {
    params.set("min_odds", state.minOdds.toString());
  }

  if (state.maxOdds !== null) {
    params.set("max_odds", state.maxOdds.toString());
  }

  state.requiredComparisonBooks.forEach((book) => {
    params.append("required_book", book);
  });

  if (state.nocache) {
    params.set("nocache", "1");
  }

  if (state.weights && Object.keys(state.weights).length > 0) {
    const parts = Object.entries(state.weights).map(
      ([book, weight]) => `${book}:${weight}`
    );
    params.set("weights", parts.join(","));
  }

  if (state.preset) {
    params.set("preset", state.preset);
  }

  if (state.requiredBooks && state.requiredBooks.length > 0) {
    params.set("required_books", state.requiredBooks.join(","));
  }

  if (state.hidePushProps) {
    params.set("hide_push_props", "1");
  }

  if (state.overUnder) {
    params.set("over_under", state.overUnder);
  }

  if (state.boost > 0) {
    params.set("boost", state.boost.toString());
  }

  return params;
}

export function statesAreEqual(a: OptimizerQueryState, b: OptimizerQueryState): boolean {
  if (a.offset !== b.offset) return false;
  if (a.limit !== b.limit) return false;
  if (a.preset !== b.preset) return false;
  if (a.requiredBooks.length !== b.requiredBooks.length) return false;
  if (!a.requiredBooks.every((book, i) => book === b.requiredBooks[i])) return false;
  return serializeQueryState(a).toString() === serializeQueryState(b).toString();
}

export function buildShareUrl(
  state: OptimizerQueryState,
  originOrEmpty?: string,
  pathname = "/optimizer"
): string {
  const params = markLink(serializeQueryState(state));

  params.delete("offset");
  params.delete("limit");
  params.delete("sort");
  params.delete("order");
  params.delete("nocache");

  const paramsStr = params.toString();
  const querySegment = paramsStr ? `?${paramsStr}` : "";
  const normalizedPath =
    pathname.startsWith("/") || pathname.startsWith("http")
      ? pathname
      : `/${pathname}`;
  if (normalizedPath.startsWith("http")) {
    return `${normalizedPath}${querySegment}`;
  }
  const base = originOrEmpty ?? "";
  return `${base}${normalizedPath}${querySegment}`;
}

export function buildClearedFilters(
  state: OptimizerQueryState
): Partial<OptimizerQueryState> {
  return {
    playerSearch: "",
    leagues: [],
    statTypes: [],
    matchups: [],
    marketScope: "",
    gameDays: [],
    startsWithin: 0,
    minEV: 0,
    offset: 0,
    slipType: "",
    minDataPoints: 0,
    minLiquidity: 0,
    minOddsToHit: 0,
    maxOddsToHit: 100,
    minOdds: null,
    maxOdds: null,
    requiredComparisonBooks: [],
    weights: null,
    preset: null,
    requiredBooks: [],
    hidePushProps: false,
    overUnder: "",
    boost: 0,
  };
}

interface ActiveFilterChipOptions {
  showHidePushProps?: boolean;
  todayYmd?: string;
}

export function shouldShowActiveFilterChips(
  state: OptimizerQueryState,
  options: ActiveFilterChipOptions = {},
): boolean {
  const { showHidePushProps = false, todayYmd = localTodayYmd() } = options;

  return (
    state.leagues.length > 0 ||
    state.statTypes.length > 0 ||
    state.matchups.length > 0 ||
    activeGameDays(state.gameDays, todayYmd).length > 0 ||
    state.startsWithin > 0 ||
    state.minEV > 0 ||
    state.minDataPoints > 0 ||
    state.minLiquidity > 0 ||
    state.requiredComparisonBooks.length > 0 ||
    state.minOddsToHit > 0 ||
    state.maxOddsToHit < 100 ||
    oddsRangeActive(state) ||
    state.playerSearch !== "" ||
    (isExplicitSlipTypePlatform(state.platform) && state.slipType !== "") ||
    state.weights !== null ||
    state.preset !== null ||
    state.requiredBooks.length > 0 ||
    (showHidePushProps && state.hidePushProps) ||
    state.overUnder !== "" ||
    state.boost > 0
  );
}

export type OptimizerStickyFilterScope = "optimizer" | "fantasy-props";

export interface OptimizerStickyFilterState {
  sportsbook: string;
  playerSearch: string;
  leagues: string[];
  statTypes: string[];
  matchups: string[];
  marketScope: MarketScope;
  slipType: string;
  minEV: number;
  gameDays: string[];
  startsWithin: number;
  minDataPoints: number;
  minLiquidity: number;
  minOddsToHit: number;
  maxOddsToHit: number;
  minOdds: number | null;
  maxOdds: number | null;
  requiredComparisonBooks: ComparisonColumnId[];
  requiredBooks: string[];
  hidePushProps: boolean;
  overUnder: OverUnder;
}

export interface OptimizerStickyPersistedFilters {
  v: 1;
  savedAt: string;
  state: OptimizerStickyFilterState;
}

export const STICKY_FILTER_DEFAULTS: OptimizerStickyFilterState = {
  sportsbook: "",
  playerSearch: "",
  leagues: [],
  statTypes: [],
  matchups: [],
  marketScope: "",
  slipType: "",
  minEV: 0,
  gameDays: [],
  startsWithin: 0,
  minDataPoints: 0,
  minLiquidity: 0,
  minOddsToHit: 0,
  maxOddsToHit: 100,
  minOdds: null,
  maxOdds: null,
  requiredComparisonBooks: [],
  requiredBooks: [],
  hidePushProps: false,
  overUnder: "",
};

const STICKY_URL_PARAM_KEYS = new Set([
  "sportsbook",
  "player_search",
  "league",
  "stat_type",
  "matchup",
  "market_type",
  "slip_type",
  "min_ev",
  "game_date",
  "starts_within",
  "min_data_points",
  "min_liquidity",
  "min_odds_to_hit",
  "max_odds_to_hit",
  "min_odds",
  "max_odds",
  "required_book",
  "required_books",
  "hide_push_props",
  "over_under",
]);

export function pageSearch(state: OptimizerQueryState, currentSearch: string): string {
  return keepLinkMarker(serializeQueryState(state), currentSearch).toString();
}

export function hasExplicitStickyFilterParams(
  rawSearchParams: Record<string, string | string[] | undefined>
): boolean {
  for (const key of Object.keys(rawSearchParams)) {
    if (STICKY_URL_PARAM_KEYS.has(key) && rawSearchParams[key] !== undefined) {
      return true;
    }
  }
  return false;
}

export function extractStickyFilterState(
  state: OptimizerQueryState
): OptimizerStickyFilterState {
  const hasCustomModel = state.weights !== null || state.preset !== null;
  return {
    sportsbook: state.sportsbook,
    playerSearch: state.playerSearch,
    leagues: [...state.leagues],
    statTypes: [...state.statTypes],
    matchups: [...state.matchups],
    marketScope: state.marketScope,
    slipType: state.slipType,
    minEV: state.minEV,
    gameDays: [...state.gameDays],
    startsWithin: state.startsWithin,
    minDataPoints: state.minDataPoints,
    minLiquidity: state.minLiquidity,
    minOddsToHit: state.minOddsToHit,
    maxOddsToHit: state.maxOddsToHit,
    minOdds: state.minOdds,
    maxOdds: state.maxOdds,
    requiredComparisonBooks: [...state.requiredComparisonBooks],
    requiredBooks: hasCustomModel ? [] : [...state.requiredBooks],
    hidePushProps: state.hidePushProps,
    overUnder: state.overUnder,
  };
}

export function applyStickyFilterState(
  base: OptimizerQueryState,
  sticky: OptimizerStickyFilterState
): OptimizerQueryState {
  const sportsbook = sticky.sportsbook || base.sportsbook;
  const platform = GLOBAL_ACTIONABLE_BOOK_IDS.includes(sportsbook as PlatformType)
    ? (sportsbook as PlatformType)
    : base.platform;
  const slipType = normalizeSlipTypeId(platform, sticky.slipType);

  const hasCustomModel = base.weights !== null || base.preset !== null;

  return {
    ...base,
    sportsbook,
    platform,
    playerSearch: sticky.playerSearch,
    leagues: [...sticky.leagues],
    statTypes: [...sticky.statTypes],
    matchups: [...sticky.matchups],
    marketScope: sticky.marketScope,
    slipType,
    minEV: sticky.minEV,
    gameDays: [...sticky.gameDays],
    startsWithin: sticky.startsWithin,
    minDataPoints: sticky.minDataPoints,
    minLiquidity: sticky.minLiquidity,
    minOddsToHit: sticky.minOddsToHit,
    maxOddsToHit: sticky.maxOddsToHit,
    minOdds: sticky.minOdds,
    maxOdds: sticky.maxOdds,
    requiredComparisonBooks: [...sticky.requiredComparisonBooks],
    requiredBooks: hasCustomModel ? base.requiredBooks : [...sticky.requiredBooks],
    hidePushProps: sticky.hidePushProps,
    overUnder: sticky.overUnder,
  };
}

export function coerceOptimizerStickyPayload(
  value: unknown
): OptimizerStickyPersistedFilters | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const obj = value as Record<string, unknown>;
  if (obj.v !== 1 || !obj.state || typeof obj.state !== "object" || Array.isArray(obj.state)) return null;

  const raw = obj.state as Record<string, unknown>;

  const rawSportsbook = typeof raw.sportsbook === "string" ? normalizeSportsbookId(raw.sportsbook) || raw.sportsbook.toLowerCase() : "";
  const sportsbook = rawSportsbook && GLOBAL_ACTIONABLE_BOOK_IDS.includes(rawSportsbook as PlatformType)
    ? rawSportsbook
    : "";
  const playerSearch = typeof raw.playerSearch === "string" ? raw.playerSearch : "";
  const leagues = coerceStringArray(raw.leagues);
  const statTypes = coerceStringArray(raw.statTypes);
  const matchups = coerceStringArray(raw.matchups);
  const marketScope = normalizeMarketScope(typeof raw.marketScope === "string" ? raw.marketScope : undefined);
  const slipType = typeof raw.slipType === "string" ? raw.slipType : "";
  const minEV = coerceFiniteNumber(raw.minEV, 0, 0);
  const gameDays = coerceGameDays(raw.gameDays);
  const startsWithin = coerceStartsWithin(raw.startsWithin);
  const minDataPoints = coerceFiniteNumber(raw.minDataPoints, 0, 0);
  const minLiquidity = Math.round(coerceFiniteNumber(raw.minLiquidity, 0, 0));

  let minOddsToHit = coerceFiniteNumber(raw.minOddsToHit, 0, 0, 100);
  let maxOddsToHit = coerceFiniteNumber(raw.maxOddsToHit, 100, 0, 100);
  if (minOddsToHit > maxOddsToHit) {
    [minOddsToHit, maxOddsToHit] = [maxOddsToHit, minOddsToHit];
  }
  const { minOdds, maxOdds } = orderOddsRange(parseAmericanBound(raw.minOdds), parseAmericanBound(raw.maxOdds));

  const requiredComparisonBooks = canonicalComparisonBooks(raw.requiredComparisonBooks);
  const requiredBooks = dedupeStringArray(coerceStringArray(raw.requiredBooks).map(b => b.toLowerCase()));
  const hidePushProps = raw.hidePushProps === true;
  const overUnder = normalizeOverUnder(typeof raw.overUnder === "string" ? raw.overUnder : undefined);

  const savedAt = typeof obj.savedAt === "string" ? obj.savedAt : new Date(0).toISOString();

  return {
    v: 1,
    savedAt,
    state: {
      sportsbook,
      playerSearch,
      leagues,
      statTypes,
      matchups,
      marketScope,
      slipType,
      minEV,
      gameDays,
      startsWithin,
      minDataPoints,
      minLiquidity,
      minOddsToHit,
      maxOddsToHit,
      minOdds,
      maxOdds,
      requiredComparisonBooks,
      requiredBooks,
      hidePushProps,
      overUnder,
    },
  };
}

export function stickyFilterStatesEqual(
  a: OptimizerStickyFilterState,
  b: OptimizerStickyFilterState
): boolean {
  return (
    a.sportsbook === b.sportsbook &&
    a.playerSearch === b.playerSearch &&
    a.slipType === b.slipType &&
    a.marketScope === b.marketScope &&
    a.minEV === b.minEV &&
    a.startsWithin === b.startsWithin &&
    a.minDataPoints === b.minDataPoints &&
    a.minLiquidity === b.minLiquidity &&
    a.minOddsToHit === b.minOddsToHit &&
    a.maxOddsToHit === b.maxOddsToHit &&
    a.minOdds === b.minOdds &&
    a.maxOdds === b.maxOdds &&
    a.hidePushProps === b.hidePushProps &&
    a.overUnder === b.overUnder &&
    arraysEqual(a.gameDays, b.gameDays) &&
    arraysEqual(a.leagues, b.leagues) &&
    arraysEqual(a.statTypes, b.statTypes) &&
    arraysEqual(a.matchups, b.matchups) &&
    arraysEqual(a.requiredBooks, b.requiredBooks) &&
    arraysEqual([...a.requiredComparisonBooks].sort(), [...b.requiredComparisonBooks].sort())
  );
}

export function hasStickyFiltersActive(state: OptimizerStickyFilterState): boolean {
  return !stickyFilterStatesEqual(state, STICKY_FILTER_DEFAULTS);
}


function coerceStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string" && v.length > 0);
}

function dedupeStringArray(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

function coerceFiniteNumber(value: unknown, fallback: number, min?: number, max?: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  let n = value;
  if (min !== undefined && n < min) n = min;
  if (max !== undefined && n > max) n = max;
  return n;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
